import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Custom keyframes for glow
const sustainableGlow = {
  boxShadow: [
    '0 0 0 0 rgba(34,197,94,0.4)',
    '0 0 0 8px rgba(34,197,94,0.2)',
    '0 0 0 0 rgba(34,197,94,0.4)'
  ]
};

export default function Results({ result, loading }) {
  /* 1. Loading spinner */
  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        className="py-16 flex justify-center"
      >
        <div className="relative w-40 h-40">
          <motion.div
            className="absolute inset-0 rounded-full"
            style={{ background: 'conic-gradient(#3B82F6 0deg 200deg, transparent 200deg 360deg)' }}
            animate={{ rotate: 360 }}
            transition={{ duration: 2, ease: 'linear', repeat: Infinity }}
          />
          <div className="absolute inset-3 rounded-full bg-gray-900 flex items-center justify-center">
            <span className="text-blue-300 font-medium">Solving…</span>
          </div>
        </div>
      </motion.div>
    );
  }

  /* 2. Guard – nothing solved yet */
  if (!result) {
    return (
      <div className="text-center text-blue-300 py-8 font-semibold">
        No results available. Please solve an ecosystem first.
      </div>
    );
  }

  /* 3. Build view model */
  const ok = result.sustainable;
  const pill = ok
    ? { text: '✨ Sustainable', cls: 'bg-green-900/20 border-green-500/30 text-green-300' }
    : { text: '🛑 Unsustainable', cls: 'bg-red-900/20 border-red-500/30 text-red-300' };

  /* depth / temp */
  let depth = 'Unknown';
  let temp = 'Unknown';
  if (result.depthTemperature) {
    if (typeof result.depthTemperature === 'string') {
      const parts = result.depthTemperature.split(',');
      depth = parts[0]?.trim() || 'Unknown';
      temp = parts[1]?.trim() || 'Unknown';
    } else {
      const { minDepth, maxDepth, minTemp, maxTemp } = result.depthTemperature;
      depth = `${minDepth ?? ''}-${maxDepth ?? ''}m`;
      temp = `${minTemp ?? ''}-${maxTemp ?? ''}°C`;
    }
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="ecosystem-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="mx-auto w-full max-w-2xl rounded-2xl overflow-hidden border border-blue-800/30 bg-gradient-to-br from-gray-950/80 to-blue-950/60 shadow-xl"
        style={ok ? { boxShadow: '0 0 32px 0 rgba(34,197,94,0.25), 0 8px 32px rgba(0,0,0,0.3)' } : {}}
      >
        {/* header */}
        <div className="flex items-center justify-between px-3 md:px-6 py-3 md:py-4 bg-gradient-to-r from-gray-900 to-blue-900">
          <div className="flex items-center gap-2 md:gap-3">
            <span className="text-xl md:text-2xl">📊</span>
            <h2 className="text-white font-semibold text-base md:text-lg tracking-wide">Ecosystem Analysis</h2>
          </div>
          <motion.span
            className={`px-2 md:px-3 py-1 rounded-lg text-xs md:text-sm font-medium border ${pill.cls} ${ok ? 'animate-pulse' : ''}`}
            animate={ok ? sustainableGlow : {}}
            transition={ok ? { duration: 1.5, repeat: Infinity, repeatType: 'loop' } : {}}
            style={ok ? { filter: 'drop-shadow(0 0 8px #22c55e88)' } : {}}
          >
            {pill.text}
          </motion.span>
        </div>

        {/* body */}
        <div className="p-4 md:p-8 space-y-6 md:space-y-8">
          {/* ecosystem name */}
          <div className="flex items-center gap-2 md:gap-3 text-blue-400 text-sm md:text-base font-medium">
            <span>🌎</span>
            <span>Ecosystem Name</span>
            <span className="text-blue-100 font-normal">{result.ecosystemName || 'Unknown'}</span>
          </div>

          {/* ecosystem status */}
          <div className="flex items-center gap-2 md:gap-3 text-blue-400 text-sm md:text-base font-medium">
            <span>🌱</span>
            <span>Ecosystem Status</span>
            <span className={ok ? 'text-green-300 font-semibold' : 'text-red-300 font-semibold'}>
              {pill.text}
            </span>
          </div>

          {/* producers */}
          <div className="bg-blue-900/30 border border-blue-500/20 rounded-lg p-3 md:p-4 space-y-2 md:space-y-3">
            <div className="flex items-center gap-2 text-blue-100 font-medium mb-1 md:mb-2 text-sm md:text-base">
              <span>🌿</span>
              Producers
            </div>
            {(result.producers || []).map((p, i) => (
              <div key={p} className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-md bg-gray-800 text-gray-300 text-sm md:text-base font-semibold">
                  {i + 1}
                </span>
                <span className="text-blue-200 text-sm md:text-base">{p}</span>
              </div>
            ))}
          </div>

          {/* animals */}
          <div className="bg-blue-900/30 border border-blue-500/20 rounded-lg p-3 md:p-4 space-y-2 md:space-y-3">
            <div className="flex items-center gap-2 text-blue-100 font-medium mb-1 md:mb-2 text-sm md:text-base">
              <span>🦁</span>
              Best Animals
            </div>
            {(result.chosenAnimals || []).map((a, i) => (
              <div key={a} className="flex items-center gap-2">
                <span className="inline-flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-md bg-gray-800 text-gray-300 text-sm md:text-base font-semibold">
                  {String.fromCharCode(65 + i)}
                </span>
                <span className="text-blue-200 text-sm md:text-base">{a}</span>
              </div>
            ))}
          </div>

          {/* ranges */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2 md:gap-3 text-blue-400 text-sm md:text-base font-medium">
              <span>🌊</span>
              <span>Depth Range</span>
              <span className="text-blue-100 font-normal">{depth}</span>
            </div>
            <div className="flex items-center gap-2 md:gap-3 text-blue-400 text-sm md:text-base font-medium">
              <span>🌡️</span>
              <span>Temperature Range</span>
              <span className="text-blue-100 font-normal">{temp}</span>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
} 